import { cn } from "@/libs/utils";
import Footer from "@/v2-components/footer";
import { createContext, useContext } from "react";
import Image from "next/image";
import GlobalNavigationBar from "@/v2-components/global-navigation-bar";
import PromoBanner from "@/components/banner/PromoBanner";
import ReferralBanner from "@/components/banner/ReferralBanner";
import { useLocale } from "@/store/locale-store";
import SuggestLocale from "@/components/SuggestLocale";

interface BaseProps {
  children?: React.ReactNode;
}

interface PageProps {
  theme?: "light" | "dark";
  imageSrc?: string;
  countryCode?: string;
}

const PageContext = createContext<PageProps | null>(null);

const usePageContext = () => {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error(
      "PageTemplate compound components cannot be rendered outside the PageTemplate component",
    );
  }
  return context;
};

const Gap = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn("pt-12 laptop:pt-16", className)}>{children}</div>
);

const Max = ({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) => {
  return (
    <div className={cn("mx-auto w-full max-w-screen-max", className)}>
      {children}
    </div>
  );
};

function Root({
  imageSrc,
  theme = "light",
  countryCode = undefined,
  children,
}: PageProps & BaseProps) {
  return (
    <PageContext.Provider value={{ imageSrc, theme, countryCode }}>
      {children}
    </PageContext.Provider>
  );
}

function Cover({
  className,
  src,
  alt = "",
  children,
  ...props
}: Omit<React.ComponentProps<typeof Image>, "src" | "alt"> & {
  src?: string;
  alt?: string;
}) {
  const { imageSrc } = usePageContext();

  if (children) return <>{children}</>;

  return (
    <Image
      className={cn(className)}
      src={src ?? imageSrc ?? ""}
      alt={alt}
      {...props}
    />
  );
}

function Content({
  children,
  className,
  useCustomNav = false,
  hidePromoBanner = false,
}: {
  children?: React.ReactNode;
  className?: string;
  useCustomNav?: boolean;
  /**
   * 상단 프로모 띠만 끈다 — **그 캠페인의 목적지 페이지에서** 쓴다.
   *
   * 이미 그 페이지에 온 사람에게 같은 곳으로 가는 광고는 잉여이고, 높이만 차지해
   * 본문을 밀어낸다. `useCustomNav` 는 GNB 까지 통째로 없애서 과하다.
   *
   * 소비처: `/event/gta5-giveaway/` (#5655). 다음 캠페인도 자기 목적지에서 이걸 쓴다.
   */
  hidePromoBanner?: boolean;
}) {
  const { countryCode } = usePageContext();
  const locale = useLocale();
  const canSuggeset = !!countryCode && locale.countryCode !== countryCode;

  return (
    <div className={cn("relative w-full", className)}>
      {!useCustomNav && (
        <div className="sticky top-0 z-40" data-sticky-header>
          {canSuggeset && <SuggestLocale countryCode={countryCode} />}
          <ReferralBanner />
          <GlobalNavigationBar />
          {!hidePromoBanner && <PromoBanner />}
        </div>
      )}
      {children}
      <Footer />
    </div>
  );
}

const PageTemplate = Object.assign(Root, {
  Cover,
  Content,
  Gap,
  Max,
});

export default PageTemplate;
